import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Sheet,
  Stack,
  Divider,
  Alert,
  CircularProgress
} from '@mui/joy';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../redux/store';
import { getSetupIntent } from '../../../apiconfig/api';
import { upsertMeeting, getCurrentUser } from '../../../apiconfig/api';
import { useMeeting } from '../../../context/MeetingContext';
import { useSession } from '../../../context/SessionContext';
import { useEmployer } from '../../../context/EmployerContext';

const STRIPE_PUBLIC_KEY = process.env.REACT_APP_STRIPE_PUBLIC_KEY;

if (!STRIPE_PUBLIC_KEY) {
  throw new Error("Missing STRIPE_PUBLIC_KEY in environment variables");
}

const stripePromise = loadStripe(STRIPE_PUBLIC_KEY);

interface PaymentFormProps {
  clientSecret: string;
}

const PaymentForm: React.FC<PaymentFormProps> = ({ clientSecret }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const stripe = useStripe();
  const elements = useElements();
  const meeting = useMeeting();
  const { sessionEmployerId } = useSession();
  const { employer } = useEmployer();
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentRequest, setPaymentRequest] = useState<any>(null);
  const [canMakePayment, setCanMakePayment] = useState(false);

  useEffect(() => {
    if (!stripe) return;

    const pr = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: {
        label: 'GEEK Staffing Services',
        amount: 0, // We'll set this to 0 since this is just for saving payment method
      },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    pr.on('paymentmethod', async (event) => {
      setIsLoading(true);
      setError(null);

      try {
        const { error: confirmError } = await stripe.confirmCardSetup(clientSecret, {
          payment_method: event.paymentMethod.id,
        });

        if (confirmError) {
          setError(confirmError.message || 'Setup failed');
        } else {
          // Payment method saved successfully
          navigate('/hiring/candidates', { 
            state: { 
              paymentMethodSaved: true,
              paymentMethodId: event.paymentMethod.id
            } 
          });
        }
      } catch (err) {
        setError('An unexpected error occurred');
      } finally {
        setIsLoading(false);
      }
    });

    pr.canMakePayment().then((result) => {
      setCanMakePayment(!!result);
      setPaymentRequest(pr);
    });
  }, [stripe, clientSecret, navigate]);

  // Persist a meeting after payment confirmation
  const persistMeeting = async () => {
    // 1. Get meetingId and meetingPayload from context
    const meetingId = meeting.id;
    // 2. Get employerId from session context
    const employerId = sessionEmployerId;
    if (!employerId) throw new Error('No employer ID in session');
    // 3. Prepare meeting payload from context
    const meetingPayload = {
      scheduledAt: meeting.scheduledAt.toISOString(),
      duration: meeting.duration,
      location: meeting.location,
      notes: meeting.notes,
      timezone: meeting.timezone,
      applicantIds: meeting.applicantIds,
    };
    await upsertMeeting(employerId, meetingId, meetingPayload);
  };

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsLoading(true);
    setError(null);

    try {
      const { error: confirmError } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/hiring/meetings`,
        },
        redirect: 'if_required',
      });

      if (confirmError) {
        setError(confirmError.message || 'Setup failed');
      } else {
        // Persist the meeting
        try {
          await persistMeeting();
          navigate(`/hiring/meetings/${meeting.id}/success`);
        } catch (persistErr) {
          // Optionally handle meeting persist error
          console.error('Failed to persist meeting:', persistErr);
        }
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f4f4f4',
        padding: '20px',
      }}
    >
      <Sheet
        sx={{
          width: '100%',
          maxWidth: '600px',
          padding: '40px',
          borderRadius: '16px',
          backgroundColor: '#ffffff',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Stack spacing={4}>
          {/* Header */}
          <Stack spacing={2} alignItems="center" textAlign="center">
            <Typography 
              level="h1" 
              fontSize="32px" 
              sx={{ 
                fontWeight: 600, 
                color: '#1A1B1D',
                marginBottom: '8px'
              }}
            >
              Payment Method
            </Typography>
            <Typography 
              level="description"
              sx={{ 
                fontSize: '16px',
                color: '#7B7F85',
                lineHeight: 1.5,
                maxWidth: '500px'
              }}
            >
              Add your payment method for future use. You won't be charged until we successfully place a candidate.
            </Typography>
          </Stack>

          <Divider sx={{ borderColor: '#E5E7EB' }} />

          {/* Error Alert */}
          {error && (
            <Alert color="danger" variant="soft">
              {error}
            </Alert>
          )}

          {/* Payment Form */}
          <form onSubmit={handleManualSubmit}>
            <Stack spacing={3}>
              {/* Apple Pay / Google Pay */}
              {canMakePayment && paymentRequest && (
                <Box
                  sx={{
                    padding: '16px',
                    borderRadius: '12px',
                    backgroundColor: '#F8F9FA',
                    border: '1px solid #E5E7EB',
                  }}
                >
                  <Typography 
                    level="body-sm" 
                    sx={{ 
                      fontSize: '14px',
                      color: '#7B7F85',
                      marginBottom: '12px'
                    }}
                  >
                    Quick payment with:
                  </Typography>
                  <div
                    id="payment-request-button"
                    style={{
                      height: '48px',
                      backgroundColor: '#2e2d7e',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: '500',
                    }}
                    onClick={() => paymentRequest.show()}
                  >
                    Pay with Apple Pay / Google Pay
                  </div>
                </Box>
              )}

              {/* Manual Card Entry */}
              <Box
                sx={{
                  padding: '24px',
                  borderRadius: '12px',
                  backgroundColor: '#F8F9FA',
                  border: '1px solid #E5E7EB',
                }}
              >
                <Typography 
                  level="h4"
                  sx={{ 
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#1A1B1D',
                    marginBottom: '16px'
                  }}
                >
                  {canMakePayment ? 'Or enter card details manually:' : 'Enter your card details:'}
                </Typography>
                <PaymentElement 
                  options={{
                    layout: 'tabs',
                    defaultValues: {
                      billingDetails: {
                        name: '',
                        email: '',
                      },
                    },
                  }}
                />
              </Box>

              {/* Buttons */}
              <Stack spacing={2}>
                <Button
                  type="submit"
                  variant="solid"
                  fullWidth
                  loading={isLoading}
                  disabled={!stripe}
                  sx={{
                    height: '56px',
                    fontSize: '18px',
                    fontWeight: 600,
                    backgroundColor: '#2e2d7e',
                    '&:hover': {
                      backgroundColor: '#23225f',
                    },
                    '&:disabled': {
                      backgroundColor: '#2e2d7e',
                      opacity: 0.6,
                    },
                  }}
                >
                  {isLoading ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size="sm" color="primary" />
                      <Typography>Saving payment method...</Typography>
                    </Stack>
                  ) : (
                    'Save Payment Method'
                  )}
                </Button>
              </Stack>
            </Stack>
          </form>

          {/* Security Notice */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography 
              level="body-sm" 
              sx={{ 
                color: '#7B7F85', 
                fontSize: '14px',
                fontWeight: 400
              }}
            >
              🔒 Your payment information is secure and encrypted by Stripe
            </Typography>
          </Box>
        </Stack>
      </Sheet>
    </Box>
  );
};

const PaymentPage: React.FC = () => {
  const { sessionEmployerId } = useSession();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const createSetupIntent = async () => {
      if (!sessionEmployerId) {
        setError('No employer ID available. Please sign in again.');
        setIsLoading(false);
        return;
      }

      try {
        // Create a setup intent to save payment method
        const response = await getSetupIntent({employerId: sessionEmployerId});
        setClientSecret(response.data.client_secret);
      } catch (err) {
        console.error('Error creating setup intent:', err);
        setError('Failed to initialize payment. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    createSetupIntent();
  }, [sessionEmployerId]);

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f4f4f4',
        }}
      >
        <CircularProgress size="lg" />
      </Box>
    );
  }

  if (error || !clientSecret) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f4f4f4',
          padding: '20px',
        }}
      >
        <Alert color="danger" variant="soft" sx={{ maxWidth: '400px' }}>
          {error || 'Unable to load payment form. Please try again.'}
        </Alert>
      </Box>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#2e2d7e',
            colorBackground: '#ffffff',
            colorText: '#1A1B1D',
            colorDanger: '#ef4444',
            fontFamily: 'Inter, system-ui, sans-serif',
            spacingUnit: '4px',
            borderRadius: '8px',
          },
        },
      }}
    >
      <PaymentForm clientSecret={clientSecret} />
    </Elements>
  );
};

export default PaymentPage; 